import React, {
  useEffect, useRef, useState,
} from 'react';
import { useLanguageStore } from '$/store/languageStore';
import useThemeStore from '$/store/useThemeStore';
import RoboneoModal from '$/components/Modal';
import style from './index.module.scss';

interface ReportModalProps {
  userInfo: { uid: string; screenName: string; avatar: string; } | undefined;
  env: 'pre' | 'beta' | 'release';
  client_id: string;
  gid: string;
  channel: string;
  language: string;
  onClose: () => void;
  open: boolean;
  getAccessToken: () => string;
}

/**
 * 在 COEP（如 team_studio）页面嵌入跨域举报 iframe 前设置 credentialless，
 * 否则浏览器会拦截加载；需在赋值 src 之前设置。
 *
 * @param iframe 举报 iframe 元素
 * @param src 举报页地址
 */
function applyCredentiallessIframeSrc(iframe: HTMLIFrameElement | null, src: string) {
  if (!iframe || !src) return;
  const el = iframe as HTMLIFrameElement & { credentialless?: boolean };
  const needReload = !el.credentialless || iframe.getAttribute('src') !== src;
  Reflect.set(iframe, 'credentialless', true);
  if (needReload) {
    Reflect.set(iframe, 'src', src);
  }
}

function ReportModal(props: ReportModalProps) {
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    client_id,
    env,
    gid,
    channel,
    onClose,
    open,
    getAccessToken,
    language,
    userInfo,
  } = props;
  const languageStore = useLanguageStore((state) => state.languageStore);
  const theme = useThemeStore((state) => state.theme);
  const [origin, setOrigin] = useState('');
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const originRef = useRef(origin);
  originRef.current = origin;

  const preFix = env === 'release' ? '' : `${env}-`;
  const iframeSrc = `https://${preFix}feedback.meitu.com/report/submit?client_id=${client_id}&theme=${theme}`;

  useEffect(() => {
    const init = async () => {
      const {
        bindHandlers,
        businessMessenger,
        setMessengerOrigin,
      } = await import('@meitu/feedback-web-bridge/es/business');

      businessMessenger.setTarget(iframeSrc);

      bindHandlers({
        getAppInfo() {
          return new Promise((resolve) => {
            resolve({
              gid,
              language,
              channel,
              theme,
            });
          });
        },
        // 跨域时必须提供该方法
        getMeituAccountEncryptedToken() {
          return {
            encryptedToken: getAccessToken() || '',
          };
        },
        getUserInfo() {
          return new Promise((resolve) => {
            resolve({
              /* 大账号UID */
              uid: userInfo?.uid,
              /* 用户昵称 */
              screenName: userInfo?.screenName,
              /* 头像地址 */
              avatar: userInfo?.avatar,
            });
          });
        },
      });
      setOrigin(setMessengerOrigin(iframeSrc));
    };
    init();
    // 与历史实现一致：仅在 open 变化时重绑 messenger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !origin) return;
    applyCredentiallessIframeSrc(iframeRef.current, origin);
  }, [open, origin]);

  /**
   * Modal destroyOnClose 后 iframe 重新挂载时立刻写入 credentialless + src。
   *
   * @param el iframe 元素
   */
  function handleIframeRef(el: HTMLIFrameElement | null) {
    iframeRef.current = el;
    if (el && originRef.current) {
      applyCredentiallessIframeSrc(el, originRef.current);
    }
  }

  return (
    <RoboneoModal
      open={open}
      wrapClassName={style.reportModal}
      bodyClassName={style.reportModalBody}
      onCancel={onClose}
      closable
      destroyOnClose
    >
      <div className={style.title}>{languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_user_report)}</div>
      <iframe ref={handleIframeRef} title="report" src={origin || undefined} />
    </RoboneoModal>

  );
}
export default ReportModal;
